import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { useFunction } from '@/hooks/useFunction';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Busy, Field, PageHeader, Panel } from '@/components/kit';
import { ListBody, PickSelect } from '@/components/inputs';
import VerifierResult, { VerifiedBadge } from '@/components/verifier/VerifierResult';
import { planSummary } from '@/shared/pricing';
import { rows } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const { isNotNull } = window.ApperSDK.CoreSDK;

export const route = { path: '/account/verifier', layout: 'owner', access: "profile === 'Customer_c'" };
export const nav = {
  icon: 'ShieldCheck',
  label: 'IPTV Verifier',
  section: 'My account',
  profiles: ['Customer_c'],
  order: 25,
};

const LINE_FIELDS = [
  'Name',
  'plan_c',
  'term_c',
  'connections_c',
  'product_c',
  'status_c',
  'line_username_c',
  'line_verified_at_c',
  'line_verified_plan_c',
  'CreatedOn',
];

const SEND_EMAIL = import.meta.env.VITE_SEND_EMAIL;

/** Best-effort result email. Only for a definite outcome, not when the server could not be reached. */
function emailResult(orderId, res) {
  if (!SEND_EMAIL || !orderId) return;
  if (res.signIn?.ok === false && res.signIn?.reason === 'unreachable') return;
  if (res.signIn?.ok && res.genuine === null) return;
  try {
    Promise.resolve(sdk.functions.invoke(SEND_EMAIL, { mode: 'verifier-result', orderId })).catch(() => {});
  } catch {
    // never blocks the check
  }
}

function loadLines() {
  return sdk
    .table('orders_c')
    .select(LINE_FIELDS)
    .where(isNotNull('line_username_c'))
    .orderByDesc('CreatedOn')
    .limit(50)
    .fetch()
    .then(rows);
}

export default function Verifier() {
  const lines = useFetch(loadLines, []);
  const list = lines.data ?? [];
  const [orderId, setOrderId] = useState('');
  const [result, setResult] = useState(null);
  const verifier = useFunction(import.meta.env.VITE_IPTV_VERIFIER);

  useEffect(() => {
    if (!orderId && list.length) setOrderId(String(list[0].Id));
  }, [list, orderId]);

  const picked = list.find((o) => String(o.Id) === orderId);

  async function verify() {
    if (!orderId) return;
    setResult(null);
    const res = await verifier.invoke({ mode: 'verify', orderId: Number(orderId) });
    if (!res) return;
    setResult(res);
    lines.run();
    emailResult(Number(orderId), res);
  }

  const labels = Object.fromEntries(
    list.map((o) => [String(o.Id), `${o.Name} · ${planSummary(o)}${o.line_verified_at_c ? ' · Verified' : ''}`]),
  );

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        eyebrow="My account"
        title="IPTV Verifier"
        description="Check that your line is a genuine Ghost Cable line and that it signs in. We use the line saved on your order, so there is nothing to type."
      />

      <Panel title="Pick a line" icon="ScanSearch" bodyClassName="py-4">
        <ListBody
          loading={lines.loading}
          data={lines.data}
          error={lines.error}
          run={lines.run}
          rows={2}
          isEmpty={!list.length}
          empty={{
            icon: 'Radio',
            title: 'No lines to check yet',
            description: 'Your line shows up here once we have set it up on one of your orders.',
            action: (
              <Button asChild size="sm" variant="outline">
                <Link to="/account/orders">See my orders</Link>
              </Button>
            ),
          }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <Field label="Order" htmlFor="verify-order" className="min-w-0 flex-1">
              <PickSelect
                id="verify-order"
                value={orderId}
                onChange={(v) => {
                  setOrderId(v);
                  setResult(null);
                }}
                values={list.map((o) => String(o.Id))}
                labels={labels}
                placeholder="Choose an order"
                disabled={verifier.loading}
              />
            </Field>
            <Button onClick={verify} disabled={!orderId || verifier.loading} className="sm:w-44">
              <Busy
                busy={verifier.loading}
                label={
                  <>
                    <ApperIcon name="ShieldCheck" size={16} />
                    Verify my line
                  </>
                }
                busyLabel="Checking…"
              />
            </Button>
          </div>
          {picked?.line_verified_at_c && !result && (
            <p className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <VerifiedBadge at={picked.line_verified_at_c} />
              Last verified {formatLocalDate(picked.line_verified_at_c, DATE_FORMATS.SHORT)}
            </p>
          )}
          {verifier.loading && (
            <p className="mt-3 text-xs text-muted-foreground" aria-live="polite">
              Signing in to your line and reading the channel list. This can take up to a minute.
            </p>
          )}
        </ListBody>
      </Panel>

      {result && (
        <Panel title="Result" icon="Fingerprint" bodyClassName="py-4" className="mt-6">
          <VerifierResult result={result} />
        </Panel>
      )}

      <p className="mt-6 text-xs text-muted-foreground">
        We do not keep a history of checks. A line that passes gets a Verified badge on the order, and we email you the
        result. The badge comes off if the line stops signing in or stops matching.
      </p>
    </div>
  );
}
